'use client';

import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { jwtDecode } from 'jwt-decode';
import { FaRegCheckCircle, FaRegTimesCircle } from "react-icons/fa";
import {
    HiOutlineUser,
    HiOutlineEnvelope,
    HiOutlinePhone,
    HiOutlineBriefcase,
    HiOutlineCheck,
    HiOutlineUserCircle,
    HiChevronDown
} from "react-icons/hi2";
import { useToast } from "../../components/ui/ToastContext";
import { API_URL } from "../../config";

type RoleData = {
    role_id: number;
    role_name: string;
    role: string;
    is_active: boolean;
    user_name_surname: string;
    login: string;
    password: string;
    producer_or_producer_group_id: number;
    relation_type_id: number;
    user_id: number;
    created_timestamp: string;
    mail: string;
    phone: number;
};

interface AccountDetailsFormProps {
    onSaveChanges: () => void;
}

export const AccountDetailsForm: React.FC<AccountDetailsFormProps> = ({ onSaveChanges }) => {
    const { showToast } = useToast();
    const location = useLocation();
    const { item: initialData } = location.state || {};
    const [selectedEditRowData, setSelectedEditRowData] = useState<RoleData | null>(initialData || null);

    const [Rol, setRol] = useState<{ role_id: number; role_name: string }[]>([]);
    const [defaultOption, setDefaultOption] = useState<string>(initialData?.role_id ? String(initialData.role_id) : '');
    const [Nume_prenume, setNume_prenume] = useState<string>(initialData?.user_name_surname || initialData?.login || '');
    const [mail, setMail] = useState<string>(initialData?.mail || initialData?.email || '');
    const [phone, setPhone] = useState<string>(initialData?.phone ? String(initialData.phone) : '');
    const [isActive, setIsActive] = useState<boolean>(initialData?.is_active !== undefined ? Boolean(initialData.is_active) : true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showGrid, setShowGrid] = useState<boolean>(true);
    const [emailError, setEmailError] = useState<boolean>(false);

    const token = localStorage.getItem('jwtToken');

    const validateEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleChangem = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setMail(value);
        if (value && !validateEmail(value)) {
            setEmailError(true);
        } else {
            setEmailError(false);
        }
    };

    const handleChangep = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value.replace(/\D/g, '');
        setPhone(rawValue);
    };

    const fetchUsers = async () => {
        try {
            let targetUserId = initialData?.user_id;
            if (!targetUserId && token) {
                try {
                    const decoded: any = jwtDecode(token);
                    targetUserId = decoded.userId;
                } catch (e) {
                    console.error(e);
                }
            }

            if (!targetUserId) return;

            const response = await axios.get(`${API_URL}/api/users`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            const matchedUser = response.data.find((item: any) => item.user_id === targetUserId);
            if (matchedUser) {
                setSelectedEditRowData(matchedUser);
                setNume_prenume(matchedUser.user_name_surname || matchedUser.login || '');
                setMail(matchedUser.mail || '');
                setPhone(matchedUser.phone ? String(matchedUser.phone) : '');
                setDefaultOption(matchedUser.role_id ? String(matchedUser.role_id) : '');
                setIsActive(Boolean(matchedUser.is_active));
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, [initialData]);

    useEffect(() => {
        axios
            .get(`${API_URL}/api/rol`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            })
            .then((response) => {
                setRol(response.data);
            })
            .catch((error) => {
                console.error("Error fetching role data:", error);
            });
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowGrid(false);
        }, 500);
        return () => clearTimeout(timer);
    }, []);

    const handleSaveChanges = async () => {
        const targetUserId = selectedEditRowData?.user_id || initialData?.user_id;
        if (!targetUserId) return;

        if (mail && !validateEmail(mail)) {
            showToast({
                message: "Formatul adresei de email este invalid!",
                type: "error",
                icon: <FaRegTimesCircle className="text-red-600 dark:text-red-500" />,
            });
            return;
        }

        try {
            await axios.put(
                `${API_URL}/api/updateuser/${targetUserId}`,
                {
                    user_name_surname: Nume_prenume,
                    login: selectedEditRowData?.login || initialData?.login,
                    role_id: defaultOption,
                    is_active: isActive ? 1 : 0,
                    password: selectedEditRowData?.password || initialData?.password,
                    producer_or_producer_group_id: selectedEditRowData?.producer_or_producer_group_id || initialData?.producer_or_producer_group_id,
                    relation_type_id: selectedEditRowData?.relation_type_id || initialData?.relation_type_id,
                    mail: mail,
                    phone: phone ? parseInt(phone, 10) : 0,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    }
                }
            );

            await axios.put(
                `${API_URL}/api/usersdelete/${targetUserId}`,
                {
                    userId: targetUserId,
                    newIsActive: isActive ? 1 : 0,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    }
                }
            );

            showToast({
                message: "Modificările au fost salvate cu succes!",
                type: "success",
                icon: <FaRegCheckCircle className="text-emerald-600 dark:text-emerald-500" />,
            });
            onSaveChanges();
            fetchUsers();
        } catch (error) {
            console.error("Error saving changes:", error);
            showToast({
                message: "A apărut o eroare la salvarea modificărilor.",
                type: "error",
                icon: <FaRegTimesCircle className="text-red-600 dark:text-red-500" />,
            });
        }
    };

    const handleButtonClick = async () => {
        setIsLoading(true);
        try {
            await handleSaveChanges();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full h-full">
            {showGrid ? (
                <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-xs animate-pulse flex flex-col justify-between h-full min-h-[300px]">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-4">
                        <div className="h-11 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                        <div className="h-11 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                        <div className="h-11 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                        <div className="h-11 bg-gray-100 dark:bg-gray-700/60 rounded-xl"></div>
                    </div>
                    <div className="h-10 w-44 bg-gray-200 dark:bg-gray-700 rounded-xl self-end"></div>
                </div>
            ) : (
                <div className="bg-white dark:bg-gray-800 border border-[#E7EAF0] dark:border-gray-700/60 rounded-2xl p-6 shadow-xs flex flex-col justify-between h-full min-h-[300px] transition-all duration-200">
                    {/* Header */}
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-[#ECE8FF] text-[#6755E8] dark:bg-indigo-950/60 dark:text-indigo-400 flex items-center justify-center shrink-0">
                                <HiOutlineUserCircle className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-sm font-bold text-[#1E293B] dark:text-white leading-tight">
                                    Informații profil
                                </h3>
                                <p className="text-xs text-[#64748B] dark:text-gray-400 mt-0.5 leading-tight">
                                    Actualizează informațiile tale personale.
                                </p>
                            </div>
                        </div>

                        {/* Status Toggle Pill */}
                        <button
                            type="button"
                            onClick={() => setIsActive(!isActive)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                                isActive
                                    ? "bg-[#E8F8EE] text-[#128842] hover:bg-[#d8f5e1] dark:bg-emerald-950/60 dark:text-emerald-300"
                                    : "bg-[#FEECEB] text-[#E03131] hover:bg-[#fad3d1] dark:bg-rose-950/60 dark:text-rose-300"
                            }`}
                            title="Apasă pentru a schimba statusul contului"
                        >
                            <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-[#18A957]' : 'bg-[#E03131]'}`}></span>
                            <span>{isActive ? "Activ" : "Inactiv"}</span>
                        </button>
                    </div>

                    {/* Form Fields */}
                    <form onSubmit={(e) => e.preventDefault()} className="mt-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* Nume Prenume */}
                            <div>
                                <label className="block text-xs font-semibold text-[#1E293B] dark:text-gray-200 mb-1.5">
                                    Nume Prenume
                                </label>
                                <div className="relative">
                                    <HiOutlineUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="text"
                                        placeholder="Nume Prenume"
                                        value={Nume_prenume}
                                        onChange={(e) => setNume_prenume(e.target.value)}
                                        className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#DDE2EA] hover:border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-gray-700/50 text-xs font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-gray-400 focus:border-[#6755E8] focus:ring-2 focus:ring-[#6755E8]/10 focus:outline-none transition-all"
                                    />
                                </div>
                            </div>

                            {/* Rol */}
                            <div>
                                <label className="block text-xs font-semibold text-[#1E293B] dark:text-gray-200 mb-1.5">
                                    Rol
                                </label>
                                <div className="relative">
                                    <HiOutlineBriefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10" />
                                    <select
                                        value={defaultOption}
                                        onChange={(e) => setDefaultOption(e.target.value)}
                                        className="w-full h-11 pl-10 pr-10 rounded-xl border border-[#DDE2EA] hover:border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-gray-700/50 text-xs font-medium text-[#1E293B] dark:text-gray-100 focus:border-[#6755E8] focus:ring-2 focus:ring-[#6755E8]/10 focus:outline-none transition-all cursor-pointer appearance-none"
                                    >
                                        {Rol.length === 0 && defaultOption ? (
                                            <option value={defaultOption}>
                                                {initialData?.role_name || (defaultOption === "1" ? "Admin" : defaultOption === "2" ? "Manager" : "User")}
                                            </option>
                                        ) : (
                                            Rol.map((role) => (
                                                <option key={role.role_id} value={role.role_id}>
                                                    {role.role_name}
                                                </option>
                                            ))
                                        )}
                                    </select>
                                    <HiChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none z-10" />
                                </div>
                            </div>

                            {/* Poștă electronică */}
                            <div>
                                <label className="block text-xs font-semibold text-[#1E293B] dark:text-gray-200 mb-1.5">
                                    Poștă electronică
                                </label>
                                <div className="relative">
                                    <HiOutlineEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="email"
                                        placeholder="Posta@electronica.com"
                                        value={mail}
                                        onChange={handleChangem}
                                        className={`w-full h-11 pl-10 pr-4 rounded-xl border bg-white dark:bg-gray-700/50 text-xs font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:outline-none transition-all ${
                                            emailError
                                                ? "border-red-500 focus:border-red-500 focus:ring-red-500/10"
                                                : "border-[#DDE2EA] hover:border-[#CBD5E1] dark:border-gray-600 focus:border-[#6755E8] focus:ring-[#6755E8]/10"
                                        }`}
                                    />
                                </div>
                                {emailError && (
                                    <p className="text-red-500 text-[10px] font-medium mt-1 leading-none">
                                        Email invalid (ex: nume@domeniu.md).
                                    </p>
                                )}
                            </div>

                            {/* Număr de telefon */}
                            <div>
                                <label className="block text-xs font-semibold text-[#1E293B] dark:text-gray-200 mb-1.5">
                                    Număr de telefon
                                </label>
                                <div className="relative">
                                    <HiOutlinePhone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                                    <input
                                        type="tel"
                                        placeholder="+373..."
                                        value={phone}
                                        onChange={handleChangep}
                                        className="w-full h-11 pl-10 pr-4 rounded-xl border border-[#DDE2EA] hover:border-[#CBD5E1] dark:border-gray-600 bg-white dark:bg-gray-700/50 text-xs font-medium text-[#1E293B] dark:text-gray-100 placeholder:text-gray-400 focus:border-[#6755E8] focus:ring-2 focus:ring-[#6755E8]/10 focus:outline-none transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Save Action Footer */}
                        <div className="flex justify-end pt-3">
                            <button
                                type="button"
                                onClick={handleButtonClick}
                                disabled={isLoading}
                                className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-[#1E293B] hover:bg-[#0F172A] text-white text-xs font-semibold shadow-xs transition-all duration-150 hover:-translate-y-0.5 cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
                            >
                                {isLoading ? (
                                    <svg className="animate-spin w-4 h-4 text-white" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                                    </svg>
                                ) : (
                                    <HiOutlineCheck className="w-4 h-4" />
                                )}
                                <span>{isLoading ? "Se salvează..." : "Salvează modificările"}</span>
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
};
